import { Box, IconButton, Typography } from "@mui/material";
import { m } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MotionContainer, varFade } from "src/components/animate";
import useGetLogo from "src/components/logo/hooks/use-logo";
import LanguagePopover from "src/layouts/shared/header/language-popover";
import { PATH_AUTH } from "src/routes/paths";

const BANNER = "/assets/Banner1.webp";
const MINT = "#b8952f80";

/* ── small decorative blob shape ── */
const Blob = ({ sx }) => (
  <Box
    sx={{
      position: "absolute",
      width: 80,
      height: 80,
      borderRadius: "16px",
      backgroundColor: "rgba(255,255,255,0.65)",
      transform: "rotate(30deg)",
      pointerEvents: "none",
      ...sx,
    }}
  />
);

const AuthLayout = ({ children, isRegister = false }) => {
  const logo = useGetLogo();
  const navigate = useNavigate();
  const tabValue = isRegister ? 1 : 0;

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: MINT,
        position: "relative",
        overflow: "hidden",
        p: { xs: 2, md: 3 },
      }}
    >
      {/* decorative background blobs */}
      <Blob sx={{ top: -20, left: -20, width: 110, height: 110 }} />
      <Blob sx={{ top: 40, left: 60, width: 50, height: 50, opacity: 0.5 }} />
      <Blob sx={{ bottom: -24, right: 80, width: 90, height: 90 }} />
      <Blob sx={{ top: 20, right: 160, width: 40, height: 40, opacity: 0.4 }} />
      <Blob
        sx={{ bottom: 60, left: 120, width: 36, height: 36, opacity: 0.5 }}
      />

      {/* ── Main card ── */}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          maxWidth: "1080px",
          minHeight: { xs: "auto", md: "580px" },
          borderRadius: "24px",
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.13)",
          backgroundColor: "#fff",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* ── Left – form panel ── */}
        <Box
          sx={{
            flex: { xs: "0 0 100%", md: "0 0 48%" },
            display: "flex",
            flexDirection: "column",
            p: { xs: 3, sm: "36px 40px" },
            backgroundColor: "#fff",
            minHeight: "100%",
          }}
        >
          {/* Header row: logo + language */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 4,
            }}
          >
            <img
              src={logo}
              style={{ height: "50px", width: "auto" }}
              alt="logo"
            />
            <Box sx={{ display: { sm: "block" } }}>
              <LanguagePopover />
            </Box>
          </Box>

          {/* Form content – vertically centered */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            {children}
          </Box>

          {/* Footer */}
          <Typography
            variant="caption"
            sx={{
              mt: 6, // Increased from 3
              textAlign: "center",
              color: "text.disabled",
              display: "block",
              fontSize: "11px",
              letterSpacing: "0.05em",
            }}
          >
            © {new Date().getFullYear()} Luxya. All rights reserved.
          </Typography>
        </Box>

        {/* ── Right – image card ── */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            alignItems: "stretch",
            // p: "16px 16px 16px 0",
          }}
        >
          <Box
            sx={{
              flex: 1,
              borderRadius: "16px",
              overflow: "hidden",
              position: "relative",
              backgroundColor: "#1a1610",
              backgroundImage: `url(${BANNER})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            {/* banner image with premium slow zoom */}
            <Box
              component={m.img}
              initial={{ scale: 1.15 }}
              animate={{ scale: 1 }}
              transition={{
                duration: 15,
                ease: "linear",
                repeat: Infinity,
                repeatType: "reverse",
              }}
              src={BANNER}
              alt="Luxya Banner"
              loading="eager"
              fetchpriority="high"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
              }}
            />

            {/* Subtle fading mist overlay */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 0%, transparent 70%)",
                animation: "mistMove 8s ease-in-out infinite alternate",
                "@keyframes mistMove": {
                  "0%": { opacity: 0.3, transform: "translateX(-10px)" },
                  "100%": { opacity: 0.6, transform: "translateX(10px)" },
                },
              }}
            />

            {/* bottom text overlay */}
            <Box
              component={MotionContainer}
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                p: "40px 32px",
                background:
                  "linear-gradient(0deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)",
                borderBottomLeftRadius: "16px",
                borderBottomRightRadius: "16px",
              }}
            >
              <Typography
                component={m.h2}
                variants={varFade().inUp}
                sx={{
                  color: "#fff",
                  fontWeight: 800,
                  lineHeight: 1.2,
                  fontSize: "24px",
                  mb: 1,
                  letterSpacing: "0.02em",
                  textShadow: "0 2px 10px rgba(0,0,0,0.3)",
                }}
              >
                MODERN <span style={{ color: "#b8952f" }}>CLASSIC</span>
              </Typography>
              <Typography
                component={m.p}
                variants={varFade().inUp}
                sx={{
                  mb: 3,
                  color: "rgba(255,255,255,0.85)",
                  fontSize: "14px",
                  lineHeight: 1.5,
                  fontWeight: 400,
                  maxWidth: "300px",
                }}
              >
                A timeless signature scent crafted for the modern individual.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* right arrow — decorative, outside card */}
    </Box>
  );
};

export default AuthLayout;
